/**
 * Centralised mapping of domain enums → `<Badge tone emphasis>` values.
 *
 * Один источник истины для «какой цвет у статуса». Когда нужно перекрасить
 * (например, on_hold → success), правим тут — все consumer'ы подхватят:
 * ProjectStatusBadge, ProjectsPage таблица, ProjectHeader, RisksPage,
 * ProjectRisksTab, TopRisksWidget, TemplatesPage, LoadSummaryWidget,
 * CalendarPage и т.д.
 *
 * См. `docs/frontend/patterns.md §«Badges»` и `components/ui/badge.tsx`.
 */
import type { ProjectStatus } from "@/api/projects";
import type { Severity } from "@/api/risks";
import type { BadgeEmphasis, BadgeTone } from "@/components/ui/badge";

export interface ToneSpec {
  tone: BadgeTone;
  emphasis?: BadgeEmphasis;
}

/**
 * Маппинг ProjectStatus → tone (CloudDesign refined-classic, май 2026):
 * - `planned`     — info (blueprint, проект в плане)
 * - `in_progress` — success (зелёный, активная работа)
 * - `on_hold`     — caution (жёлтый, приостановлен / «Пауза»)
 * - `completed`   — neutral (серый, ушёл в архив)
 * - `cancelled`   — danger (красный, отмена)
 * Дизайнер также вводит `at_risk` (красный) — пока в backend enum нет;
 * добавим когда появится поле.
 */
export const PROJECT_STATUS_TONE: Record<ProjectStatus, ToneSpec> = {
  planned: { tone: "info" },
  in_progress: { tone: "success" },
  on_hold: { tone: "caution" },
  completed: { tone: "neutral" },
  cancelled: { tone: "danger" },
};

export const RISK_SEVERITY_TONE: Record<Severity, ToneSpec> = {
  critical: { tone: "danger", emphasis: "solid" },
  high: { tone: "danger" },
  medium: { tone: "caution" },
  low: { tone: "neutral" },
};

/**
 * Статус работы на проекте → tone (задача 114 §2.1).
 *
 * Отдельный маппинг, а не переиспользование `PROJECT_STATUS_TONE`: у работы
 * своя шкала («в работе» — текущее состояние, «завершена» — достигнутая цель),
 * поэтому `completed` здесь success, а не neutral, как у ушедшего в архив
 * проекта. Совпадение слов в двух enum'ах не делает их одной шкалой.
 * `planned` в v1 недостижим (работы приходят только из трекинга) — заведён под
 * будущую синхронизацию с Гантом.
 */
export const WORK_STATUS_TONE: Record<
  "planned" | "in_progress" | "paused" | "completed",
  ToneSpec
> = {
  planned: { tone: "neutral" },
  in_progress: { tone: "info" },
  paused: { tone: "caution" },
  completed: { tone: "success" },
};

/** Template lifecycle: active (in production) / draft / archived. */
export const TEMPLATE_STATUS_TONE: Record<"active" | "draft" | "archived", ToneSpec> = {
  active: { tone: "success" },
  draft: { tone: "caution" },
  archived: { tone: "neutral" },
};

/** Boolean active/inactive (e.g. users, units). */
export const ACTIVE_TONE = { tone: "success" } as const satisfies ToneSpec;
export const INACTIVE_TONE = { tone: "danger" } as const satisfies ToneSpec;

/**
 * Load peak ratio → tone.
 * - ratio > 1     — overload (danger)
 * - ratio ≥ 0.85  — high utilization (caution)
 * - otherwise     — calm (success)
 */
export function loadPeakTone(ratio: number): ToneSpec {
  if (ratio > 1) return { tone: "danger" };
  if (ratio >= 0.85) return { tone: "caution" };
  return { tone: "success" };
}

/**
 * Calendar daily hours coverage → tone.
 * - 0 hours      — danger (closed)
 * - <8 hours     — caution (partial)
 * - ≥8 hours     — success (full coverage)
 */
export function calendarHoursTone(hours: number): ToneSpec {
  if (hours === 0) return { tone: "danger" };
  if (hours < 8) return { tone: "caution" };
  return { tone: "success" };
}
